/**
 * Knowledge-layer enums (ADR-0022).
 *
 * `knowledge_chunk` stores both of these as `text`, not as a Postgres enum
 * type: adding a member must stay a code change, never a migration on a table
 * that will hold millions of rows. These enums are the only guard on the value
 * set, so writing a bare string literal anywhere defeats the whole arrangement.
 */

/**
 * What part of a source object a chunk represents.
 *
 * Part of the chunk's identity — the partial unique index is
 * `(tenant_id, object_type, object_id, chunk_kind, chunk_index)` — so one
 * object can carry several parallel streams of chunks without them colliding.
 *
 * Only `BODY` exists today, because phase 1 indexes `text/plain` and nothing
 * else. Document text extraction adds captions, OCR and transcripts; each is
 * one member here plus a producer, and no schema change.
 */
export enum ChunkKind {
	/** Prose extracted from the object's main text. */
	BODY = 'body',

	/**
	 * A reading of a picture: what it depicts, and the words visible in it.
	 *
	 * Separate from `BODY` because the two make different claims. `BODY` is what a
	 * document SAYS — the words are the author's, and a citation to them is a
	 * quotation. This is what a model saw when it looked at an asset, so a citation to
	 * it is a description, and a reviewer needs to be able to tell which they were
	 * handed. Since `chunk_kind` is filterable, a query that must only quote source
	 * text can exclude this kind outright.
	 *
	 * Not split further into caption-versus-detected-words: both come from the same
	 * model reading the same pixels in the same pass, and a split would imply one is
	 * more authoritative than the other.
	 *
	 * **A video frame's on-screen text is this kind too, and it carries a timestamp.** The
	 * kind encodes WHOSE CLAIM the text is, not what medium it came from — a model reading
	 * words printed on a frame is doing the same act, at the same trust level, as reading
	 * words printed on a photograph, so a compliance query that excludes machine readings
	 * of pixels must exclude both or it excludes neither usefully. A fourth member would
	 * split one provenance across two labels and make every such query name both, where
	 * forgetting one fails by silently INCLUDING generated text in a quote-only result.
	 * Telling a photograph from a frame is answered by the citation's content type, and a
	 * still's location is `null` where a frame's is a point in time.
	 */
	IMAGE = 'image',

	/**
	 * Speech transcribed from a timed medium, plus what a model said it was about.
	 *
	 * A third kind rather than reuse of either neighbour, and the reason is the same
	 * one that separates the first two: what a citation to it MEANS. `BODY` is a
	 * quotation an author wrote and can be checked against the page. This is a
	 * machine's reading of speech — a citation to it is "the recording says roughly
	 * this, at about here" — and a reviewer who cannot tell the two apart will quote
	 * a transcription error as though a person had written it.
	 *
	 * Not `IMAGE` either, despite both coming from the same reader: `IMAGE`'s
	 * location is nothing at all, this one's is a point in time, and a surface that
	 * offers "jump to 12:04" needs to know which it has.
	 *
	 * Deliberately NOT split into transcript-versus-summary. Both arrive from one
	 * pass over the same audio, and a split would imply the transcript is the
	 * authoritative half — which is backwards for retrieval, where the summary is
	 * usually the better short answer.
	 */
	TRANSCRIPT = 'transcript',
}

/**
 * Why a chunk is no longer retrievable.
 *
 * Set in the same statement that sets `is_active = false`, and `NULL` for
 * exactly as long as the row is active — a CHECK constraint enforces that
 * pairing, because the failure that actually happens is forgetting the reason,
 * not inventing a new one.
 *
 * **Retrieval never reads this column.** It filters on `is_active` alone; every
 * inactive row is equally unretrievable regardless of why. The reason exists
 * for audit, retention and diagnosis. A read path that branches on it has
 * misunderstood the column.
 *
 * Adding a member means naming its trigger, whether it is terminal, and its
 * retention class — here and in the knowledge-index spec — and teaching the
 * retention job what to do with it. An unclassified reason silently becomes
 * "keep forever" by default.
 */
export enum InactiveReason {
	/**
	 * The object's text changed and a newer generation was inserted.
	 *
	 * Terminal: the new generation is the live one. Prunable — the current text
	 * is already stored, so these rows are only history.
	 */
	SUPERSEDED = 'superseded',

	/**
	 * Every classification row for the object was removed, so nothing may reach
	 * it any more.
	 *
	 * *Not* terminal in the usual sense: reclassifying the object produces a
	 * **new** generation and these rows stay dead, because idempotence compares
	 * against the active set, which is empty.
	 *
	 * **Keep.** These rows are the evidence that a human once asserted a
	 * classification and later withdrew it — the one deactivation reason a
	 * retention job must not discard.
	 */
	REVOKED = 'revoked',

	/**
	 * The source object was deleted.
	 *
	 * Terminal. Prunable once the source row itself is purged; until then the
	 * chunks are what explain why a previously-cited passage stopped resolving.
	 */
	DELETED = 'deleted',

	/**
	 * The source withdrew the content from publication.
	 *
	 * *Not* terminal: republishing writes a **new** generation, because idempotence
	 * compares against the active set and that set is now empty.
	 *
	 * Distinct from `REVOKED`, which is about who may REACH the object, and from
	 * `DELETED`, which is about the object existing at all. This one says the object
	 * is fine and its content is deliberately not being served — the state an author
	 * creates by unpublishing, and the reason a passage can stop resolving with
	 * nothing wrong anywhere.
	 *
	 * **Keep.** Like `REVOKED`, these rows are the evidence of a human decision.
	 */
	UNPUBLISHED = 'unpublished',

	/**
	 * The chunk passed a retention or validity boundary.
	 *
	 * Terminal, prunable. Nothing writes this in phase 1 — it is here so the
	 * retention job has a value to write when one is built.
	 */
	EXPIRED = 'expired',
}

/**
 * What one indexing pass did to an object — the tag of `IndexOutcome`.
 *
 * An enum rather than bare literals because the union protects CONSTRUCTION and
 * not COMPARISON: `{ status: 'nonsense' }` is a type error, but
 * `outcome.status === 'unachored'` compiles and is simply always false. The
 * failure mode of the typo is therefore a branch that never runs — invisible,
 * and here it would silently stop the sweeper counting retirements.
 */
export enum IndexStatus {
	/** A new chunk generation was written. */
	INDEXED = 'indexed',
	/** Text already matched; the object's projected scope moved. */
	RESCOPED = 'rescoped',
	/** Text and scope both already matched — a redelivery, or a no-op re-tagging. */
	UNCHANGED = 'unchanged',
	/** No classification anchor: scope dropped, chunks revoked. */
	UNANCHORED = 'unanchored',
	/** Scope was refreshed, but the text yielded nothing to index. */
	SKIPPED = 'skipped',
}

/**
 * Whether an object holds a classification anchor — the tag of `ScopeResolution`.
 *
 * Deliberately its OWN enum rather than sharing `IndexStatus.UNANCHORED`, even
 * though the two spell one value the same way. They answer different questions —
 * "what did this pass do?" versus "is this object anchored?" — and a shared tag
 * would couple two types that have no reason to change together.
 */
export enum ScopeStatus {
	RESOLVED = 'resolved',
	UNANCHORED = 'unanchored',
}

/**
 * What a browse listing returns one row of.
 *
 * The two grains are the same filter and the same predicate over the same rows —
 * only the projection and the ordering key differ, which is why they are a
 * parameter rather than two methods.
 */
export enum BrowseGrain {
	/** One row per document, with its citation and live chunk count. */
	OBJECT = 'object',
	/** A document's chunks in document order. */
	PASSAGE = 'passage',
}

/**
 * How a browse listing is ordered.
 *
 * Both are keyset-pageable, which is the admissibility rule: an order that cannot
 * be resumed from a cursor would need offset paging, and offset over a concurrent
 * corpus skips and duplicates rows silently.
 *
 * There is deliberately no order by title — titles live on the source rows and are
 * read during citation hydration, so sorting by one would mean sorting AFTER
 * hydration, i.e. within a single page, and page two could repeat page one's items.
 */
export enum BrowseOrder {
	/** The stable key, `(object_type, object_id)`. */
	KEY = 'key',
	/** Most recently indexed first, tie-broken on the stable key. Object grain only. */
	RECENCY = 'recency',
}

/**
 * Why an object holds no retrievable chunks — the discriminator on an
 * introspection result, and the reason introspection is not "browse with a
 * flag".
 *
 * For an unindexed object a listing's answer is ABSENCE: there are no rows to
 * return, whatever flags it is given. These two values are the answer to the
 * question absence cannot carry — *why* — and they separate the two causes that
 * need different fixes: nobody classified it, or nothing could be extracted from
 * it.
 */
export enum UnindexedReason {
	/** No classification anchor, so the object is reachable by nobody. */
	NO_ANCHOR = 'no_anchor',
	/** Classified, but no text was ever extracted — reachable in principle, empty in fact. */
	NO_CHUNKS = 'no_chunks',
}

/**
 * Which document a comparison is about, described by what the caller has.
 *
 * Three, and they split on what the CALLER holds rather than on how the knowledge
 * layer fetches it. Picking one never requires knowing whether indexing has
 * finished: `REF` covers a stored object at any stage of its pipeline.
 */
export enum ProbeSourceKind {
	/** ATLAS holds it. Pass its type and id, and nothing else. */
	REF = 'ref',
	/** You hold the text. Use this for audio, video, scans and anything oversized. */
	TEXT = 'text',
	/** You hold the file. Send the bytes and let the knowledge layer read them. */
	BYTES = 'bytes',
}

/**
 * What one row of a comparison result is.
 *
 * Passage grain answers "which paragraphs of the library match mine"; object grain
 * answers "which documents does mine overlap, and by how much". Same statement body,
 * different grouping — so a parameter, not two methods, for the same reason
 * {@link BrowseGrain} is one.
 */
export enum SimilarGrain {
	/** Matched library passages, best first, each naming the probes that reached it. */
	PASSAGE = 'passage',
	/** Candidate documents with how many distinct probes each matched. */
	OBJECT = 'object',
}

/**
 * Why the document could not be read, as a stable code to branch on.
 *
 * Each names a different person's problem, and the HTTP status follows from that:
 * fix the request (422), fix the reference (404), ask for access (403), or wait and
 * retry (409 for an object not ready, 503 for a dependency that is). One shared error would make "try again in a minute" look the same as
 * "this will never work".
 */
export enum ProbeRefusalReason {
	/** No parser reads this content type in a request. Send `TEXT` instead, or upload it. 422. */
	NOT_INLINEABLE = 'not_inlineable',
	/** The object exists but has no text yet, because extraction has not run. Retry. 409. */
	NOT_EXTRACTED = 'not_extracted',
	/** No such object, or it is deleted. 404. */
	SOURCE_GONE = 'source_gone',
	/** A stored plain-text file is larger than a comparison reads into memory. Send `TEXT` instead. 422. */
	TOO_LARGE = 'too_large',
	/** The document is empty, or holds only whitespace. Nothing to compare. 422. */
	EMPTY = 'empty',
	/**
	 * The object exists and the caller may not read it. 403, not 404: retrieval already
	 * names every indexed object in its citations, so concealing existence here would
	 * cost a caller a clear answer while hiding nothing.
	 */
	NOT_AUTHORIZED = 'not_authorized',
	/**
	 * The document was read and none of its paragraphs could be embedded, because the
	 * embedding provider throttled. 503, and the only transient failure this surface
	 * has. Distinct from {@link EMPTY}, which is a fact about the document and will not
	 * change on retry.
	 */
	EMBEDDING_UNAVAILABLE = 'embedding_unavailable',
}
